import React, { useEffect, useRef, useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MIN_SIZE = 3, MAX_SIZE = 10;
const MODES = [
  { key: 'reverse', label: 'Reverse (Converge)' },
  { key: 'palindrome', label: 'Palindrome Check' },
  { key: 'pairsum', label: 'Pair Sum (Sorted)' },
  { key: 'fastslow', label: 'Fast & Slow' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const emptyArray = n => Array.from({ length: n }, () => null);

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Box row with support for two independent pointer tags per box ── */
const BoxRow = ({ array, getBoxState }) => (
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 26, paddingBottom: 22 }}>
    {array.map((val, i) => {
      const st = getBoxState ? getBoxState(i) : {};
      const bg = st.bg || 'var(--bg3)';
      const border = st.border || 'var(--border)';
      const color = st.color || 'var(--text)';
      return (
        <div key={i} style={{ position: 'relative' }}>
          {st.tags && st.tags.map((t, ti) => (
            <span key={ti} style={{
              position: 'absolute', top: -22 - ti * 15, left: '50%', transform: 'translateX(-50%)',
              fontSize: 9.5, fontWeight: 800, color: t.color, whiteSpace: 'nowrap',
            }}>{t.text}</span>
          ))}
          <div style={{
            width: 50, height: 50, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: bg, color, border: `1.5px solid ${border}`, transition: 'all 0.2s ease',
          }}>
            {val === null || val === undefined ? '·' : val}
          </div>
          <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', bottom: -18, fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>{i}</span>
        </div>
      );
    })}
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = {
  ...smallInput, width: 220,
};
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});

/* ─── Reverse (Converge) ─────────────────────────────────────────── */
const ReverseMode = () => {
  const [sizeInput, setSizeInput] = useState('6');
  const [array, setArray] = useState(emptyArray(6));
  const [insertValue, setInsertValue] = useState('');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const filledCount = array.filter(v => v !== null).length;
  const isFull = filledCount === array.length;

  const createArray = () => {
    const n = Math.min(MAX_SIZE, Math.max(MIN_SIZE, parseInt(sizeInput, 10) || MIN_SIZE));
    setArray(emptyArray(n));
    setSteps(null); setStep(0); setPlaying(false);
  };
  const insertNext = () => {
    const idx = array.findIndex(v => v === null);
    if (idx === -1 || insertValue.trim() === '' || isNaN(Number(insertValue))) return;
    const next = [...array]; next[idx] = Number(insertValue); setArray(next);
    setInsertValue('');
  };
  const startReverse = () => {
    if (!isFull) return;
    const working = [...array];
    const built = [{ left: 0, right: working.length - 1, snapshot: [...working] }];
    let l = 0, r = working.length - 1;
    while (l < r) {
      const tmp = working[l]; working[l] = working[r]; working[r] = tmp;
      built.push({ left: l, right: r, snapshot: [...working], swapped: true });
      l++; r--;
    }
    built.push({ left: l, right: r, snapshot: [...working], done: true });
    setSteps(built); setStep(0); setPlaying(false);
  };

  useEffect(() => {
    if (!playing || !steps) return;
    if (step >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setStep(s => s + 1), 900);
    return () => clearTimeout(t);
  }, [playing, step, steps]);

  const cur = steps ? steps[step] : null;
  const boxes = cur ? cur.snapshot : array;
  const getBoxState = i => {
    if (!cur) return array[i] === null ? { border: 'var(--border-strong)' } : {};
    if (cur.done) {
      if (i === cur.left) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tags: [{ text: 'meet — done', color: GREEN }] };
      return {};
    }
    if (i === cur.left && i === cur.right) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tags: [{ text: 'L = R', color: GREEN }] };
    if (i === cur.left) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags: [{ text: 'left', color: V2 }] };
    if (i === cur.right) return { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags: [{ text: 'right', color: TEAL }] };
    return {};
  };

  const code = {
    lines: [
      'int left = 0, right = arr.length - 1;',
      'while (left < right) {',
      '    int temp = arr[left];',
      '    arr[left] = arr[right];',
      '    arr[right] = temp;',
      '    left++;',
      '    right--;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 1 : (step === 0 ? 0 : 3),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow array={boxes} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Size (3–10)</span>
            <input value={sizeInput} onChange={e => setSizeInput(e.target.value)} style={smallInput} placeholder="6" />
            <button onClick={createArray} style={smallBtn(V1)}>Create Array</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Insert next</span>
            <input value={insertValue} onChange={e => setInsertValue(e.target.value)} style={smallInput} placeholder="value" disabled={isFull} />
            <button onClick={insertNext} style={smallBtn(V1, isFull)} disabled={isFull}>{isFull ? 'Array Full' : 'Insert'}</button>
            <button onClick={startReverse} style={smallBtn(GREEN, !isFull)} disabled={!isFull}>Reverse with Two Pointers</button>
          </div>
          {steps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
              <button onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))} disabled={step === steps.length - 1} style={smallBtn(V1, step === steps.length - 1)}>Next →</button>
              <button onClick={() => setPlaying(p => !p)} style={smallBtn(playing ? RED : GREEN)}>{playing ? 'Pause' : 'Auto Play'}</button>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {steps.length}</span>
            </div>
          )}
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>
            left starts at index 0, right starts at the last index. Each step swaps their values, then left moves inward (+1) and right moves inward (−1) — they converge toward the middle.
          </p>
        </div>
      </div>
      <CodePanel title="Java — Reverse, Two Pointers" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Palindrome Check ───────────────────────────────────────────── */
const PalindromeMode = () => {
  const [text, setText] = useState('racecar');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startCheck = () => {
    const s = text.trim();
    if (!s) return;
    const chars = s.split('');
    const built = [];
    let l = 0, r = chars.length - 1;
    while (l < r) {
      const match = chars[l] === chars[r];
      built.push({ left: l, right: r, match });
      if (!match) { built.push({ left: l, right: r, mismatch: true }); break; }
      l++; r--;
    }
    if (built.length === 0 || (!built[built.length - 1].mismatch)) {
      built.push({ left: l, right: r, done: true, isPalindrome: true });
    } else {
      built.push({ done: true, isPalindrome: false });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const chars = text.trim().split('');
  const getBoxState = i => {
    if (!cur || cur.done) {
      if (cur && cur.done && cur.isPalindrome) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
      return {};
    }
    if (i === cur.left && i === cur.right) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tags: [{ text: 'L = R — meet', color: GREEN }] };
    if (i === cur.left) return { bg: cur.mismatch ? `linear-gradient(135deg,${RED},#c0293a)` : `linear-gradient(135deg,${V1},${V2})`, border: cur.mismatch ? RED : V1, color: '#fff', tags: [{ text: 'left', color: cur.mismatch ? RED : V2 }] };
    if (i === cur.right) return { bg: cur.mismatch ? `linear-gradient(135deg,${RED},#c0293a)` : `linear-gradient(135deg,${TEAL},#2563EB)`, border: cur.mismatch ? RED : TEAL, color: '#fff', tags: [{ text: 'right', color: cur.mismatch ? RED : TEAL }] };
    return {};
  };

  const note = !cur ? 'Enter a word or phrase, then Check.' :
    cur.done ? (cur.isPalindrome ? 'Pointers met (or crossed) with every pair matching — it\'s a palindrome!' : 'Mismatch found — not a palindrome. No need to keep comparing.') :
    cur.mismatch ? `s[${cur.left}]='${chars[cur.left]}' ≠ s[${cur.right}]='${chars[cur.right]}' — stop immediately.` :
    `s[${cur.left}]='${chars[cur.left]}' equals s[${cur.right}]='${chars[cur.right]}' — move both pointers inward.`;

  const code = {
    lines: [
      'int left = 0, right = s.length() - 1;',
      'while (left < right) {',
      '    if (s.charAt(left) != s.charAt(right)) {',
      '        return false;',
      '    }',
      '    left++;',
      '    right--;',
      '}',
      'return true;',
    ],
    active: !cur ? -1 : cur.done ? 8 : (cur.mismatch ? 3 : 2),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow array={chars} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={text} onChange={e => { setText(e.target.value); setSteps(null); setStep(0); }} style={wideInput} placeholder="e.g. racecar" />
            <button onClick={startCheck} style={smallBtn(V1)}>Check</button>
          </div>
          {steps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
              <button onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))} disabled={step === steps.length - 1} style={smallBtn(V1, step === steps.length - 1)}>Next →</button>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {steps.length}</span>
            </div>
          )}
          <div style={{
            padding: '12px 16px', borderRadius: 10, fontSize: 13, lineHeight: 1.6,
            background: cur && cur.done ? (cur.isPalindrome ? 'rgba(46,204,113,0.1)' : 'rgba(240,65,85,0.08)') : `${V1}0c`,
            border: `1px solid ${cur && cur.done ? (cur.isPalindrome ? 'rgba(46,204,113,0.35)' : 'rgba(240,65,85,0.3)') : `${V1}25`}`,
            color: cur && cur.done ? (cur.isPalindrome ? '#1e9e57' : RED) : 'var(--text)',
            fontWeight: cur && cur.done ? 700 : 500,
          }}>{note}</div>
        </div>
      </div>
      <CodePanel title="Java — Palindrome Check" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Pair Sum (Sorted) ──────────────────────────────────────────── */
const PairSumMode = () => {
  const [arrInput, setArrInput] = useState('2, 7, 11, 15, 20');
  const [targetInput, setTargetInput] = useState('26');
  const [arr, setArr] = useState([2, 7, 11, 15, 20]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startFind = () => {
    const sorted = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)).sort((a, b) => a - b);
    const target = Number(targetInput);
    if (sorted.length < 2 || isNaN(target)) return;
    const built = [];
    let l = 0, r = sorted.length - 1;
    while (l < r) {
      const sum = sorted[l] + sorted[r];
      if (sum === target) { built.push({ left: l, right: r, sum, result: 'found' }); break; }
      if (sum < target) { built.push({ left: l, right: r, sum, result: 'toosmall' }); l++; }
      else { built.push({ left: l, right: r, sum, result: 'toobig' }); r--; }
    }
    if (built.length === 0 || built[built.length - 1].result !== 'found') {
      built.push({ done: true, notFound: true });
    }
    setArr(sorted);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const getBoxState = i => {
    if (!cur || cur.done) return {};
    if (i === cur.left && i === cur.right) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
    if (i === cur.left) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags: [{ text: 'left', color: V2 }] };
    if (i === cur.right) return { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags: [{ text: 'right', color: TEAL }] };
    return {};
  };

  const note = !cur ? 'Enter a comma-separated list (it will be sorted) and a target, then Find Pair.' :
    cur.done ? 'left and right crossed with no match — no pair sums to the target.' :
    cur.result === 'found' ? `arr[${cur.left}] + arr[${cur.right}] = ${cur.sum} — matches target! Found the pair.` :
    cur.result === 'toosmall' ? `arr[${cur.left}] + arr[${cur.right}] = ${cur.sum}, too small — move left forward (need a bigger sum).` :
    `arr[${cur.left}] + arr[${cur.right}] = ${cur.sum}, too big — move right backward (need a smaller sum).`;

  const code = {
    lines: [
      'int left = 0, right = arr.length - 1;',
      'while (left < right) {',
      '    int sum = arr[left] + arr[right];',
      '    if (sum == target) return new int[] { left, right };',
      '    else if (sum < target) left++;',
      '    else right--;',
      '}',
      'return new int[] {};',
    ],
    active: !cur ? -1 : cur.done ? 7 : (cur.result === 'found' ? 3 : cur.result === 'toosmall' ? 4 : 5),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow array={arr} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Array</span>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="2, 7, 11, 15" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
            <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={smallInput} placeholder="26" />
            <button onClick={startFind} style={smallBtn(V1)}>Find Pair</button>
          </div>
          {steps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
              <button onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))} disabled={step === steps.length - 1} style={smallBtn(V1, step === steps.length - 1)}>Next →</button>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {steps.length}</span>
            </div>
          )}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Pair Sum, Sorted Array" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Fast & Slow (Find Middle) ──────────────────────────────────── */
const FastSlowMode = () => {
  const [sizeInput, setSizeInput] = useState('7');
  const [array, setArray] = useState(emptyArray(7));
  const [insertValue, setInsertValue] = useState('');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const filledCount = array.filter(v => v !== null).length;
  const isFull = filledCount === array.length;

  const createArray = () => {
    const n = Math.min(MAX_SIZE, Math.max(MIN_SIZE, parseInt(sizeInput, 10) || MIN_SIZE));
    setArray(emptyArray(n));
    setSteps(null); setStep(0); setPlaying(false);
  };
  const insertNext = () => {
    const idx = array.findIndex(v => v === null);
    if (idx === -1 || insertValue.trim() === '' || isNaN(Number(insertValue))) return;
    const next = [...array]; next[idx] = Number(insertValue); setArray(next);
    setInsertValue('');
  };
  const startFind = () => {
    if (!isFull) return;
    const n = array.length;
    const built = [{ slow: 0, fast: 0 }];
    let slow = 0, fast = 0;
    while (fast < n - 1) {
      slow += 1;
      fast += 2;
      if (fast > n - 1) fast = n - 1;
      built.push({ slow, fast });
    }
    built.push({ slow, fast, done: true });
    setSteps(built); setStep(0); setPlaying(false);
  };

  useEffect(() => {
    if (!playing || !steps) return;
    if (step >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setStep(s => s + 1), 900);
    return () => clearTimeout(t);
  }, [playing, step, steps]);

  const cur = steps ? steps[step] : null;
  const getBoxState = i => {
    if (!cur) return array[i] === null ? { border: 'var(--border-strong)' } : {};
    const tags = [];
    if (i === cur.slow) tags.push({ text: 'slow (+1)', color: V2 });
    if (i === cur.fast) tags.push({ text: 'fast (+2)', color: TEAL });
    if (cur.done) {
      if (i === cur.slow) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tags: [{ text: 'middle!', color: GREEN }] };
      return {};
    }
    if (tags.length === 2) return { bg: `linear-gradient(135deg,${V1},${TEAL})`, border: V1, color: '#fff', tags };
    if (i === cur.slow) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags };
    if (i === cur.fast) return { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags };
    return {};
  };

  const note = !cur ? 'Build an array, then Find Middle.' :
    cur.done ? `fast reached the end — slow is sitting exactly on the middle element, index ${cur.slow}.` :
    `slow moves one step, fast moves two — fast is always exactly twice as far from the start.`;

  const code = {
    lines: [
      'int slow = 0, fast = 0;',
      'while (fast < arr.length - 1) {',
      '    slow++;',
      '    fast += 2;',
      '}',
      '// slow is now the middle index',
    ],
    active: !cur ? -1 : cur.done ? 5 : 2,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow array={array} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Size (3–10)</span>
            <input value={sizeInput} onChange={e => setSizeInput(e.target.value)} style={smallInput} placeholder="7" />
            <button onClick={createArray} style={smallBtn(V1)}>Create Array</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Insert next</span>
            <input value={insertValue} onChange={e => setInsertValue(e.target.value)} style={smallInput} placeholder="value" disabled={isFull} />
            <button onClick={insertNext} style={smallBtn(V1, isFull)} disabled={isFull}>{isFull ? 'Array Full' : 'Insert'}</button>
            <button onClick={startFind} style={smallBtn(GREEN, !isFull)} disabled={!isFull}>Find Middle</button>
          </div>
          {steps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
              <button onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))} disabled={step === steps.length - 1} style={smallBtn(V1, step === steps.length - 1)}>Next →</button>
              <button onClick={() => setPlaying(p => !p)} style={smallBtn(playing ? RED : GREEN)}>{playing ? 'Pause' : 'Auto Play'}</button>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {steps.length}</span>
            </div>
          )}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Fast & Slow Pointers" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const QUIZ_ARR = [1, 3, 5, 7, 9, 12, 16];
const buildQuizScenario = () => {
  const target = QUIZ_ARR[Math.floor(Math.random() * QUIZ_ARR.length)] + QUIZ_ARR[Math.floor(Math.random() * QUIZ_ARR.length)];
  let l = 0, r = QUIZ_ARR.length - 1;
  const hops = Math.floor(Math.random() * 3);
  for (let i = 0; i < hops && l < r; i++) {
    const sum = QUIZ_ARR[l] + QUIZ_ARR[r];
    if (sum === target) break;
    if (sum < target) l++; else r--;
  }
  const sum = QUIZ_ARR[l] + QUIZ_ARR[r];
  let correct = sum === target ? 'found' : sum < target ? 'left' : 'right';
  return { l, r, target, sum, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const getBoxState = i => {
    if (i === scenario.l && i === scenario.r) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
    if (i === scenario.l) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags: [{ text: 'left', color: V2 }] };
    if (i === scenario.r) return { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags: [{ text: 'right', color: TEAL }] };
    return {};
  };

  const options = [
    { key: 'left', label: 'Sum too small — move left forward (left++)' },
    { key: 'right', label: 'Sum too big — move right backward (right--)' },
    { key: 'found', label: 'Sum matches — pair found!' },
  ];

  return (
    <div>
      <BoxRow array={QUIZ_ARR} getBoxState={getBoxState} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        target = {scenario.target}. arr[left] + arr[right] = {scenario.sum}. What should the two pointers do next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const TwoPointerPlayground = () => {
  const [mode, setMode] = useState('reverse');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'reverse' && <ReverseMode />}
      {mode === 'palindrome' && <PalindromeMode />}
      {mode === 'pairsum' && <PairSumMode />}
      {mode === 'fastslow' && <FastSlowMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default TwoPointerPlayground;
